import jwtDecode from 'jwt-decode';
import axios from 'axios';

import { getApiBaseURL } from '../../ApiBaseURL';

// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.baseURL = getApiBaseURL();

// Loader handling
let loaderCount = 0;
const updateLoader = () => {
    window.dispatchEvent(new CustomEvent('api-loading', { detail: loaderCount > 0 }));
};

axios.interceptors.request.use(
    (config) => {
        loaderCount++;
        updateLoader();

        const franchiseId = sessionStorage.getItem('selectedFranchiseId');
        // Prevent filtering the franchise list itself
        const isFranchiseList = config.url?.includes('/franchises') && config.method === 'get';
        const isSettingsRequest = config.url?.includes('/settings');
        // Super admin messages are their own default/fallback messages — do not scope by franchise
        const currentUser = getUserFromCookie();
        const isSuperAdminMessages = config.url?.includes('/messages') && (currentUser as any)?.role === 'super_admin';

        if (franchiseId && !isFranchiseList && !isSettingsRequest && !isSuperAdminMessages) {
            config.params = { ...config.params, franchiseId };
        }

        return config;
    },
    (error) => {
        loaderCount--;
        updateLoader();
        return Promise.reject(error);
    }
);

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        loaderCount--;
        updateLoader();
        return response;
    },
    (error) => {
        loaderCount--;
        updateLoader();
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        let message;

        if (error && error.response && error.response.status === 404) {
            // window.location.href = '/not-found';
        } else if (error && error.response && error.response.status === 403) {
            window.location.href = '/access-denied';
        } else {
            switch (error.response.status) {
                case 401:
                    message =
                        error.response && error.response.data && error.response.data['message']
                            ? error.response.data['message']
                            : 'Invalid credentials';
                    break;
                case 403:
                    message = 'Access Forbidden';
                    break;
                case 404:
                    message = 'Sorry! the data you are looking for could not be found';
                    break;
                case 410: {
                    // Gone (e.g., expired/invalid password reset links)
                    const backendMessage = error.response?.data?.['message'];
                    message = Array.isArray(backendMessage)
                        ? backendMessage.join(', ')
                        : backendMessage || 'Link expired';
                    break;
                }
                default: {
                    const backendMessage = error.response?.data?.['message'];
                    message = Array.isArray(backendMessage)
                        ? backendMessage.join(', ')
                        : backendMessage || error.message || error;
                }
            }
            return Promise.reject(message);
        }
    }
);

const AUTH_SESSION_KEY = 'adminto_user';

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    else delete axios.defaults.headers.common['Authorization'];
};

const getUserFromCookie = () => {
    const user = sessionStorage.getItem(AUTH_SESSION_KEY);
    return user ? (typeof user == 'object' ? user : JSON.parse(user)) : null;
};
class APICore {
    /**
     * Fetches data from given url
     */
    get = (url: string, params?: any) => {
        return axios.get(url, {
            params: params && Object.keys(params).length > 0 ? params : undefined,
        });
    };

    getFile = (url: string, params?: any) => {
        return axios.get(url, {
            responseType: 'blob',
            params: params && Object.keys(params).length > 0 ? params : undefined,
        });
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any) => {
        return axios.post(url, data);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(url, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const user = this.getLoggedInUser();

        if (!user) {
            return false;
        }
        const decoded: any = jwtDecode(user.token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
            console.warn('access token expired');
            return false;
        } else {
            return true;
        }
    };

    setLoggedInUser = (session: any) => {
        if (session) sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        else {
            sessionStorage.removeItem(AUTH_SESSION_KEY);
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromCookie();
    };

    setUserInSession = (modifiedUser: any) => {
        const userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
        if (!userInfo) return;

        try {
            const parsed = JSON.parse(userInfo);
            if (!parsed || typeof parsed !== 'object') return;

            // Support both historic nested shape ({ token, user: {...} })
            // and current flat shape ({ id, role, token, ... }).
            const baseUser =
                parsed.user && typeof parsed.user === 'object'
                    ? { ...parsed.user, token: parsed.token ?? parsed.user.token }
                    : parsed;

            this.setLoggedInUser({
                ...baseUser,
                ...modifiedUser,
            });
        } catch (e) {
            console.error('Failed to update user session:', e);
        }
    };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
    const { token } = user;
    if (token) {
        setAuthorization(token);
    }
}

export { APICore, setAuthorization };
